import { useEffect, useRef, useState } from 'react';
import { SecurityPlusKeys } from 'CustomHooks/usePinAuth';

type Options = {
  restaurantId: string | null;
  canEdit: boolean;
  /** The editor's draft starts from the stored layout, so it must be loaded. */
  layoutLoaded: boolean;
  authenticate: (
    type: SecurityPlusKeys,
    onSubmit: (signature: string) => void | Promise<void>,
  ) => void;
};

/**
 * Opening the product editor behind the settings PIN. The editor belongs to
 * the restaurant it was opened for; a PIN that completes after a switch
 * opens nothing.
 */
const useProductEditorEntry = ({
  restaurantId,
  canEdit,
  layoutLoaded,
  authenticate,
}: Options) => {
  const [openFor, setOpenFor] = useState<string | null>(null);
  const currentRestaurant = useRef(restaurantId);
  currentRestaurant.current = restaurantId;

  useEffect(() => setOpenFor(null), [restaurantId]);
  useEffect(() => {
    if (!canEdit) setOpenFor(null);
  }, [canEdit]);

  const canOpen = !!restaurantId && canEdit && layoutLoaded;

  const open = () => {
    if (!canOpen) return;
    const requestedFor = restaurantId;
    authenticate('dashboard.settings.read', () => {
      if (currentRestaurant.current === requestedFor) setOpenFor(requestedFor);
    });
  };

  return {
    canOpen,
    editorOpen: canOpen && openFor === restaurantId,
    open,
    close: () => setOpenFor(null),
  };
};

export default useProductEditorEntry;
